import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import createImportTableStore, { ImportTableStateType, ImportTableStoreType } from '../../store/importTableStore';
import { FieldValueType, ImportDescriptorIdType, ReportIdType } from '../../../../types';
import { NUMBER_DESCRIPTOR_DATA_TYPE } from '../../constants';
import Decimal from 'decimal.js';
import { getIsScalable } from '../../store/importTableStore/utils';
import { IMPORT_DESCRIPTOR_SOURCE } from '../../../../constants/generalConstants';

const ImportTableContext = createContext<ReturnType<typeof createImportTableStore> | null>(null);

type ImportDescriptorsStoreProviderPropsType = {
    initialState: ImportTableStateType;
    children: ReactNode;
};

export function ImportTableStoreProvider({ children, initialState }: ImportDescriptorsStoreProviderPropsType) {
    const [store, setStore] = useState(() => createImportTableStore(initialState));

    useEffect(() => {
        setStore(createImportTableStore(initialState));
    }, [initialState, setStore]);

    return <ImportTableContext.Provider value={store}>{children}</ImportTableContext.Provider>;
}

export function useImportTableStore<T>(selector: (state: ImportTableStoreType) => T) {
    const store = useContext(ImportTableContext);
    if (!store) {
        throw new Error('useImportTableStore should be used in ImportTableStoreProvider');
    }
    return useStore(store, useShallow(selector));
}

export const useImportTableStoreDescriptorActions = () => useImportTableStore((state) => state.descriptorActions);

export const useImportTableStoreReportActions = () => useImportTableStore((state) => state.reportsActions);

export const useImportTableStoreDescriptorsMap = () => useImportTableStore((state) => state.descriptorsMap);

export const useImportTableStoreDescriptor = (descriptorId: ImportDescriptorIdType) =>
    useImportTableStore((state) => state.descriptorsMap[descriptorId]);

export const useImportTableStoreInitialDescriptorType = (descriptorId: ImportDescriptorIdType) =>
    useImportTableStore((state) => state.initialDescriptorsToTypes[descriptorId]);

export const useImportTableStoreDescriptorsEditMode = () =>
    useImportTableStore((state) => state.labelEditing && state.scaleEditing && state.typeEditing);

export const useImportTableStoreReportsEditMode = () =>
    useImportTableStore((state) => {
        const reports = Object.values(state.reportsMap);
        return reports.length > 0 && reports.every((report) => report.editing);
    });

export const useImportTableStoreDescriptorSupportsSignFlip = (descriptorId: ImportDescriptorIdType) =>
    useImportTableStore((state) => {
        const descriptor = state.descriptorsMap[descriptorId];
        const reports = Object.values(state.reportsMap);
        const hasCalculatedFieldValue =
            reports.length > 0 && reports.some((report) => report.rowIdToValue[descriptor.id].calculatedBy3D);
        return (
            !hasCalculatedFieldValue &&
            descriptor.dataType === NUMBER_DESCRIPTOR_DATA_TYPE &&
            descriptor.source === IMPORT_DESCRIPTOR_SOURCE.IMPORT_FILE
        );
    });

export const useImportTableStoreReportFieldValue = (
    descriptorId: ImportDescriptorIdType,
    reportId: ReportIdType,
    scaled: boolean = false
): FieldValueType => {
    const initialDescriptorDataType = useImportTableStoreInitialDescriptorType(descriptorId);
    return useImportTableStore((state) => {
        const value = state.reportsMap[reportId].rowIdToValue[descriptorId];
        const initialDescriptorScale = state.initialDescriptorsToScales[descriptorId];
        const normalizedValue = value.normalizedValue;

        const shouldScale = getIsScalable(initialDescriptorDataType, normalizedValue) && scaled;

        return shouldScale
            ? {
                  ...value,
                  normalizedValue: new Decimal(normalizedValue!).dividedBy(initialDescriptorScale).toNumber(),
                  correction: value.correction
                      ? new Decimal(value.correction).dividedBy(initialDescriptorScale).toNumber()
                      : value.correction,
              }
            : value;
    });
};

export const useImportTableStoreReportsMap = () => useImportTableStore((state) => state.reportsMap);

export const useImportTableStoreFieldError = (descriptorId: ImportDescriptorIdType, reportId: ReportIdType) =>
    useImportTableStore((state) => {
        const error = { error: false, message: '' };
        if (state.reportsErrors[reportId] && state.reportsErrors[reportId][descriptorId]) {
            error.error = state.reportsErrors[reportId][descriptorId].error;
            error.message = state.reportsErrors[reportId][descriptorId].message;
        }
        return error;
    });

export const useImportTableStoreReportsErrors = () => useImportTableStore((state) => state.reportsErrors);

export const useImportTableStoreNavigationActiveCoordinates = () =>
    useImportTableStore((state) => ({
        activeRow: state.activeCoordinates.activeRow,
        activeCol: state.activeCoordinates.activeCol,
    }));

export const useImportTableStoreCoordinatesActions = () => useImportTableStore((state) => state.coordinatesActions);

export const useImportTableStoreIsCellActive = (cellRow: number, cellCol: number) =>
    useImportTableStore(
        (state) => state.activeCoordinates.activeRow === cellRow && state.activeCoordinates.activeCol === cellCol
    );

/**
 * @description Retrieves the field values of every report for the given descriptor id
 */
export const useImportTableStoreReportsFieldValue = (descriptorId: ImportDescriptorIdType) =>
    useImportTableStore((state) =>
        Object.values(state.reportsMap).map((report) => {
            return report.rowIdToValue[descriptorId];
        })
    );
